import { useState, useCallback } from 'react';
import {
  WatsonChatResponse,
  WatsonChatDetailResponse,
  WatsonChatQueryParams,
  WatsonChatSynopsisResponse,
  WatsonChatOverviewResponse,
  WatsonChatAnswerResponse,
  ESnackbarStyle,
} from 'shared/types';
import { openNotification } from 'utils/notification-utils';
import {
  getWatsonChatSessions,
  getWatsonChatDetail,
  getWatsonChatSynopsis,
  getWatsonChatOverview,
  askWatsonChat,
} from 'services/api/watsonChatService';
import { useDebounce } from 'shared/hooks';

interface WatsonChatDataResult {
  data: WatsonChatResponse | null;
  detailData: WatsonChatDetailResponse | null;
  synopsis: WatsonChatSynopsisResponse | null;
  overview: WatsonChatOverviewResponse | null;
  isLoading: boolean;
  isDetailLoading: boolean;
  isSynopsisLoading: boolean;
  isOverviewLoading: boolean;
  filterParams: WatsonChatQueryParams;
  setFilterParams: (params: WatsonChatQueryParams) => void;
  fetchData: () => Promise<void>;
  fetchDetail: (sessionId: string) => Promise<void>;
  fetchSynopsis: (sessionId: string) => Promise<void>;
  fetchOverview: () => Promise<void>;
  askQuestion: (question: string) => Promise<WatsonChatAnswerResponse | null>;
}

const useWatsonChatData = (): WatsonChatDataResult => {
  const [data, setData] = useState<WatsonChatResponse | null>(null);
  const [detailData, setDetailData] = useState<WatsonChatDetailResponse | null>(null);
  const [synopsis, setSynopsis] = useState<WatsonChatSynopsisResponse | null>(null);
  const [overview, setOverview] = useState<WatsonChatOverviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);
  const [isSynopsisLoading, setIsSynopsisLoading] = useState<boolean>(false);
  const [isOverviewLoading, setIsOverviewLoading] = useState<boolean>(false);
  const [filterParams, setFilterParams] = useState<WatsonChatQueryParams>({});

  const debouncedParams = useDebounce(filterParams, 500);

  const fetchData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await getWatsonChatSessions(debouncedParams);
      setData(response.data);
    } catch (e: any) {
      openNotification(ESnackbarStyle.ERROR, e?.response?.data?.message || e.message || 'Failed to fetch Watson Chat sessions');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedParams]);

  const fetchDetail = useCallback(async (sessionId: string): Promise<void> => {
    setIsDetailLoading(true);
    setDetailData(null);
    setSynopsis(null);
    try {
      const response = await getWatsonChatDetail(sessionId);
      setDetailData(response.data);
    } catch (e: any) {
      openNotification(ESnackbarStyle.ERROR, e?.response?.data?.message || e.message || 'Failed to fetch chat messages');
    } finally {
      setIsDetailLoading(false);
    }
  }, []);

  const fetchSynopsis = useCallback(async (sessionId: string): Promise<void> => {
    setIsSynopsisLoading(true);
    try {
      const response = await getWatsonChatSynopsis(sessionId);
      setSynopsis(response.data);
    } catch (e: any) {
      openNotification(ESnackbarStyle.ERROR, e?.response?.data?.message || e.message || 'Failed to generate synopsis');
    } finally {
      setIsSynopsisLoading(false);
    }
  }, []);

  const fetchOverview = useCallback(async (): Promise<void> => {
    setIsOverviewLoading(true);
    setOverview(null);
    try {
      const response = await getWatsonChatOverview(debouncedParams);
      setOverview(response.data);
    } catch (e: any) {
      openNotification(ESnackbarStyle.ERROR, e?.response?.data?.message || e.message || 'Failed to generate overview');
    } finally {
      setIsOverviewLoading(false);
    }
  }, [debouncedParams]);

  const askQuestion = useCallback(async (question: string): Promise<WatsonChatAnswerResponse | null> => {
    try {
      const response = await askWatsonChat(question, debouncedParams);
      return response.data;
    } catch (e: any) {
      openNotification(ESnackbarStyle.ERROR, e?.response?.data?.message || e.message || 'Failed to get answer');
      return null;
    }
  }, [debouncedParams]);

  return {
    data,
    detailData,
    synopsis,
    overview,
    isLoading,
    isDetailLoading,
    isSynopsisLoading,
    isOverviewLoading,
    filterParams,
    setFilterParams,
    fetchData,
    fetchDetail,
    fetchSynopsis,
    fetchOverview,
    askQuestion,
  };
};

export default useWatsonChatData;
